import React, { useState } from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useFeatures } from "../../lib/features";

/**
 * <ResetWorkspaceButton /> — destructive owner-only action.
 *
 * Shown on Dashboard (top-right, subtle) and Settings › Organization.
 * Opens a confirmation dialog with the exact spec copy. On confirm it
 * calls POST /api/organizations/current/reset which wipes every business
 * collection for the org and invalidates BI/analytics cache. After a
 * successful reset it fires `onReset` so the parent can re-fetch and
 * refreshes the capability profile.
 */
export default function ResetWorkspaceButton({ variant = "ghost", onReset, testid = "reset-workspace" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { refresh: refreshFeatures } = useFeatures();

  const styles = variant === "danger"
    ? "inline-flex items-center gap-2 text-sm border border-red-500/40 bg-red-500/5 text-red-300 hover:bg-red-500/10 hover:border-red-500/60 px-4 py-2 rounded-lg transition-colors"
    : "inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-red-300 border border-zinc-800 hover:border-red-500/40 hover:bg-red-500/5 px-3 py-1.5 rounded-lg transition-colors";

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/organizations/current/reset");
      toast.success("Workspace reset — every module now shows the empty state.");
      try { await refreshFeatures(); } catch { /* non-blocking */ }
      setOpen(false);
      if (onReset) onReset(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={styles}
        data-testid={`${testid}-btn`}
      >
        <Trash2 className="h-3.5 w-3.5" />
        Reset Workspace
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid={`${testid}-dialog`}>
          <div className="absolute inset-0 bg-black/70" onClick={busy ? undefined : () => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="min-w-0">
                <h2
                  className="text-lg font-semibold tracking-tight text-zinc-50"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  Reset Workspace
                </h2>
                <p
                  className="mt-2 text-sm text-zinc-300 leading-relaxed"
                  data-testid={`${testid}-copy`}
                >
                  This permanently deletes all imported business data and returns your workspace to its initial state.
                </p>
              </div>
            </div>

            <ul className="mt-5 rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-xs text-zinc-400 space-y-1.5">
              <li>· Imports · Orders · Order items</li>
              <li>· Customers · Products · Categories</li>
              <li>· Opportunities · AI briefings · AI conversations</li>
              <li>· Business Intelligence &amp; Analytics caches</li>
            </ul>

            <div className="mt-6 flex justify-end gap-2">
              <button
                disabled={busy}
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm disabled:opacity-50"
                data-testid={`${testid}-cancel`}
              >
                Cancel
              </button>
              <button
                disabled={busy}
                onClick={run}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={`${testid}-confirm`}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {busy ? "Resetting…" : "Reset Workspace"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
